import Analytics from '@withlocals/withlocals-analytics';
import { AddFluxHOCProps, addFlux } from 'components/wrappers/WithFlux';
import {
  createFilter,
  getFilter,
  getMoreFilters,
  getSubOption,
} from 'data/filters';
import cloneDeep from 'lodash.clonedeep';
import { Filter } from 'model/Filters';
import { Aggregations } from 'model/SearchResults';
import routeToSearchURL from 'modules/homescreen/helpers/routeToSearchURL';
import React, { ComponentType, PureComponent } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { wrapDisplayName } from 'recompose';

import { STATUS_PENDING } from '../../../../../constants';
import MoreFiltersAmountOfResultsProvider from '../../../MoreFiltersAmountOfResultsProvider';

interface MoreFiltersConnectorProps {
  initialSelectedFilters: Filter[];
  onClose?: ({
    source,
    callback,
  }: {
    source: 'escape button' | 'clear button' | 'outside click' | 'apply button';
    callback?: () => void;
  }) => void;
}

interface MoreFiltersConnectorState {
  selectedFilters: Filter[];
  aggregations: Aggregations;
}

export interface MoreFiltersConnectorWrappedProps {
  selectedFilters: Filter[];
  aggregations: Aggregations;
  onKeywordAdd: (keyword: string) => void;
  onKeywordRemove: (keyword: string) => void;
  onFilterToggle: (category: any, subOption: any) => void;
  onToursToggle: () => void;
  onToursSubcategoryToggle: (e: any) => void;
  onActivitiesToggle: () => void;
  onActivitiesSubcategoryToggle: (e: any) => void;
  onTransportModeToggle: () => void;
  onTransportModeSubOptionToggle: (e: any) => void;
  onPriceSubOptionToggle: (e: any) => void;
  onDurationSubOptionToggle: (e: any) => void;
  onApply: (
    source:
      | 'escape button'
      | 'clear button'
      | 'outside click'
      | 'apply button'
      | 'enter button',
  ) => (e: any) => void;
  onClear: () => void;
  amountOfResults: number;
  resultsLoading: boolean;
}

const MoreFiltersConnector = function () {
  return (
    WrappedComponent: ComponentType<MoreFiltersConnectorWrappedProps>,
  ): ComponentType<MoreFiltersConnectorProps> => {
    const klass = class MoreFiltersConnectorInternal extends PureComponent<
      MoreFiltersConnectorProps & RouteComponentProps & AddFluxHOCProps,
      MoreFiltersConnectorState
    > {
      static displayName = wrapDisplayName(
        WrappedComponent,
        'MoreFiltersConnector',
      );

      timeout = undefined;
      state = {
        selectedFilters: this.props.initialSelectedFilters || [],
        aggregations: this.props.flux.stores.SearchStore.getAggregations(
          this.props.flux.stores.FiltersStore.getFilters(),
        ),
      };

      componentDidMount() {
        this.props.flux.stores.FiltersStore.on('change', this.onStoreChange);
        this.props.flux.stores.SearchStore.on('change', this.onStoreChange);
      }

      componentDidUpdate(prevProps, prevState) {
        const { selectedFilters } = this.state;
        const { flux } = this.props;

        if (selectedFilters !== prevState.selectedFilters) {
          const searchResults = flux.stores.SearchStore.getSearchResult(
            this.getFilters(),
          );
          if (searchResults?.status !== STATUS_PENDING) {
            this.timeout = setTimeout(() =>
              flux.actions.search.fetchSearchResults(
                this.getFilters(),
                this.getSorting(),
              ),
            );
          }
        }
      }

      componentWillUnmount() {
        this.props.flux.stores.FiltersStore.removeListener(
          'change',
          this.onStoreChange,
        );
        this.props.flux.stores.SearchStore.removeListener(
          'change',
          this.onStoreChange,
        );
        clearTimeout(this.timeout);
      }

      onStoreChange = () => {
        this.setState({
          aggregations: this.props.flux.stores.SearchStore.getAggregations(
            this.props.flux.stores.FiltersStore.getFilters(),
          ),
        });
      };

      getFilters() {
        const { country, city, lang } =
          this.props.flux.stores.FiltersStore.getFilters();
        const newFilters = cloneDeep(this.state.selectedFilters);

        return {
          lang,
          country,
          city,
          page: 1,
          activeFilters: newFilters,
        };
      }

      getSorting() {
        return this.props.flux.stores.SearchStore.getSorting();
      }

      onFilterToggle = (category, subOption) => {
        const newSelectedCategories = cloneDeep(this.state.selectedFilters);

        let selectedCategory = newSelectedCategories.find(
          (selCategory) => selCategory.uid === category.uid,
        );
        if (!selectedCategory) {
          selectedCategory = createFilter(category.uid);
          selectedCategory.subOptions = [];
          selectedCategory.value = undefined;
          newSelectedCategories.push(selectedCategory);
        } else if (category === subOption) {
          newSelectedCategories.pop(selectedCategory);
        }

        if (category.subOptions.length > 0 && subOption) {
          const subcategoryIndex = selectedCategory.subOptions.some(
            (selSub) => selSub.id === subOption.id,
          );
          if (subcategoryIndex) {
            selectedCategory.subOptions = selectedCategory.subOptions.filter(
              (selSub) => selSub.id !== subOption.id,
            );
          } else {
            selectedCategory.subOptions.push(subOption);
          }
        } else {
          if (selectedCategory.value === category.value) {
            selectedCategory.value = '';
          } else {
            selectedCategory.value = category.value;
          }
        }

        this.setState({ selectedFilters: newSelectedCategories });
      };

      onCategoryToggle(
        category: 'tours' | 'activities',
        locationFilteredSubCategories,
      ) {
        let newSelectedFilters = cloneDeep(this.state.selectedFilters);
        const activityType =
          newSelectedFilters.find(
            (filter) => filter.uid === 'activityType.activity',
          ) || createFilter('activityType.activity');

        if (
          activityType.subOptions.find(
            (subOption) => subOption.value === category,
          )
        ) {
          // Turn off category filter
          activityType.subOptions = activityType.subOptions.filter(
            (subOption) => subOption.value !== category,
          );
        } else {
          // Turn on category filter
          newSelectedFilters = newSelectedFilters.filter(
            (filter) => filter.uid !== 'activityType.activity',
          );
          activityType.subOptions.push(
            getSubOption('activityType.activity', category),
          );
          newSelectedFilters.push(activityType);
          const subCategories = newSelectedFilters.find(
            (filter) => filter.uid === 'subcategory.items[]',
          );
          if (subCategories) {
            // Turn off subCategories of tour filter
            newSelectedFilters = newSelectedFilters.filter(
              (filter) => filter.uid !== 'subcategory.items[]',
            );
            subCategories.subOptions = subCategories.subOptions.filter(
              (filter) =>
                !locationFilteredSubCategories.some(
                  (subOption) => subOption.value === filter.value,
                ),
            );
            newSelectedFilters.push(subCategories);
          }
        }

        this.setState({ selectedFilters: newSelectedFilters });
      }

      onSubCategoryToggle(
        category: 'tours' | 'activities',
        value: string,
        locationFilteredSubCategories,
      ) {
        let newSelectedFilters = cloneDeep(this.state.selectedFilters);
        const subCategories =
          newSelectedFilters.find(
            (filter) => filter.uid === 'subcategory.items[]',
          ) || createFilter('subcategory.items[]');
        const activityType =
          newSelectedFilters.find(
            (filter) => filter.uid === 'activityType.activity',
          ) || createFilter('activityType.activity');

        // If category is turned on, turn off category and turn on all subcategories of that category except the one clicked on
        if (
          activityType.subOptions.find(
            (subOption) => subOption.value === category,
          )
        ) {
          // Turn off category filter
          activityType.subOptions = activityType.subOptions.filter(
            (subOption) => subOption.value !== category,
          );
          // Turn on all subcategories of category except the one clicked on
          newSelectedFilters = newSelectedFilters.filter(
            (filter) => filter.uid !== 'subcategory.items[]',
          );

          locationFilteredSubCategories
            .filter((subOption) => subOption.value !== value)
            .forEach((subOption) => subCategories.subOptions.push(subOption));
          newSelectedFilters.push(subCategories);
        } else {
          // Just toggle
          if (
            subCategories.subOptions.find(
              (subOption) => subOption.value === value,
            )
          ) {
            // Turn off
            subCategories.subOptions = subCategories.subOptions.filter(
              (subOption) => subOption.value !== value,
            );
          } else {
            // Turn on
            newSelectedFilters = newSelectedFilters.filter(
              (filter) => filter.uid !== 'subcategory.items[]',
            );
            subCategories.subOptions.push(
              getSubOption('subcategory.items[]', value),
            );
            newSelectedFilters.push(subCategories);

            // Are all subcategories turned on? Then turn on the category and turn off all subcategories
            if (
              locationFilteredSubCategories.every((a) =>
                subCategories.subOptions.some((b) => a.value === b.value),
              )
            ) {
              this.onCategoryToggle(category, locationFilteredSubCategories);
              return;
            }
          }
        }

        this.setState({ selectedFilters: newSelectedFilters });
      }

      onToursToggle = () =>
        this.onCategoryToggle(
          'tours',
          getMoreFilters(this.state.aggregations).toursSubcategoriesFilters,
        );

      onToursSubcategoryToggle = (e) => {
        const { value } = e.currentTarget;
        this.onSubCategoryToggle(
          'tours',
          value,
          getMoreFilters(this.state.aggregations).toursSubcategoriesFilters,
        );
      };

      onActivitiesToggle = () =>
        this.onCategoryToggle(
          'activities',
          getMoreFilters(this.state.aggregations)
            .activitiesSubcategoriesFilters,
        );

      onActivitiesSubcategoryToggle = (e) => {
        const { value } = e.currentTarget;
        this.onSubCategoryToggle(
          'activities',
          value,
          getMoreFilters(this.state.aggregations)
            .activitiesSubcategoriesFilters,
        );
      };

      onToggleSubOption(filterName, subOptionValue) {
        let newSelectedFilters = cloneDeep(this.state.selectedFilters);
        const transportModeFilter =
          newSelectedFilters.find((filter) => filter.uid === filterName) ||
          createFilter(filterName);

        if (
          transportModeFilter.subOptions.find(
            (subOption) => subOption.value === subOptionValue,
          )
        ) {
          // Turn off
          transportModeFilter.subOptions =
            transportModeFilter.subOptions.filter(
              (subOption) => subOption.value !== subOptionValue,
            );
        } else {
          // Turn on
          newSelectedFilters = newSelectedFilters.filter(
            (filter) => filter.uid !== filterName,
          );
          transportModeFilter.subOptions.push(
            getSubOption(filterName, subOptionValue),
          );
          newSelectedFilters.push(transportModeFilter);
        }

        this.setState({ selectedFilters: newSelectedFilters });
      }

      onTransportModeToggle = () => {
        let newSelectedFilters = cloneDeep(this.state.selectedFilters);
        const transportModeFilter =
          newSelectedFilters.find(
            (filter) => filter.uid === 'other.transport_mode.items[]',
          ) || createFilter('other.transport_mode.items[]');
        const { transportModeSubOptions } = getMoreFilters(
          this.state.aggregations,
        );

        if (
          transportModeFilter.subOptions.length ===
          transportModeSubOptions.length
        ) {
          newSelectedFilters = newSelectedFilters.filter(
            (filter) => filter.uid !== 'other.transport_mode.items[]',
          );
        } else {
          newSelectedFilters = newSelectedFilters.filter(
            (filter) => filter.uid !== 'other.transport_mode.items[]',
          );
          transportModeFilter.subOptions = transportModeSubOptions;
          newSelectedFilters.push(transportModeFilter);
        }
        this.setState({ selectedFilters: newSelectedFilters });
      };

      onTransportModeSubOptionToggle = (e) => {
        const { value } = e.currentTarget;
        this.onToggleSubOption('other.transport_mode.items[]', value);
      };

      onPriceSubOptionToggle = (e) => {
        const { value } = e.currentTarget;
        this.onToggleSubOption('price.items[]', value);
      };

      onDurationSubOptionToggle = (e) => {
        const { value } = e.currentTarget;
        this.onToggleSubOption('duration.items[]', value);
      };

      onKeywordRemove = (keyword: string) => {
        const keywordsFilter = this.state.selectedFilters.find(
          (filter) => filter.uid === 'keywords',
        );
        const oldKeywordsValue = keywordsFilter?.value ?? '';

        const newKeywordsFilter = getFilter('keywords');
        newKeywordsFilter.value = oldKeywordsValue
          .replace(keyword, '')
          .trim()
          .toLowerCase();

        const newSelectedCategories = cloneDeep(this.state.selectedFilters);
        const index = newSelectedCategories.findIndex(
          (filter) => filter.uid === 'keywords',
        );

        if (index === -1) {
          newSelectedCategories.push(newKeywordsFilter);
        } else {
          newSelectedCategories[index] = newKeywordsFilter;
        }

        this.setState({ selectedFilters: newSelectedCategories });
      };

      onKeywordAdd = (keyword: string) => {
        const keywordsFilter = this.state.selectedFilters.find(
          (filter) => filter.uid === 'keywords',
        );
        const oldKeywordsValue = keywordsFilter?.value ?? '';
        const keywordIndex = oldKeywordsValue.indexOf(keyword);

        if (keyword.trim().length === 0 || keywordIndex > -1) {
          return;
        }

        const newKeywordsFilter = getFilter('keywords');
        newKeywordsFilter.value = `${oldKeywordsValue} ${keyword}`
          .trim()
          .toLowerCase();

        const newSelectedCategories = cloneDeep(this.state.selectedFilters);
        const index = newSelectedCategories.findIndex(
          (filter) => filter.uid === 'keywords',
        );

        if (index === -1) {
          newSelectedCategories.push(newKeywordsFilter);
        } else {
          newSelectedCategories[index] = newKeywordsFilter;
        }

        this.setState({ selectedFilters: newSelectedCategories });
      };

      isPristine() {
        const { selectedFilters } = this.state;
        const { initialSelectedFilters } = this.props;

        if (selectedFilters.length !== initialSelectedFilters.length) {
          return false;
        }

        return selectedFilters.every((selectedFilter) => {
          return initialSelectedFilters.some((initialSelectedFilter) => {
            const sameValue =
              selectedFilter.value === initialSelectedFilter.value;
            const sameSubOptions =
              selectedFilter.subOptions.length ===
                initialSelectedFilter.subOptions.length &&
              selectedFilter.subOptions.every((subOption) => {
                return initialSelectedFilter.subOptions.some(
                  (initialSubOption) => initialSubOption.id === subOption.id,
                );
              });
            return sameValue && sameSubOptions;
          });
        });
      }

      onApply =
        (source: 'escape button' | 'outside click' | 'apply button') => (e) => {
          e?.stopPropagation();

          const filtersToAdd = [...this.state.selectedFilters];

          if (this.isPristine()) {
            if (!this.props.onClose) {
              routeToSearchURL({ filtersToAdd, replace: true });
            } else {
              this.props.onClose({
                source,
                callback: () =>
                  routeToSearchURL({ filtersToAdd, replace: true }),
              });
            }
            return;
          }

          const keywordsFilter = this.state.selectedFilters.find(
            (filter) => filter.uid === 'keywords',
          );
          const keywords = keywordsFilter?.value ?? '';

          Analytics.track('Search page', 'Filter category', {
            selectedCategories: this.state.selectedFilters,
            keywordsFilter: keywords,
          });

          if (!this.props.onClose) {
            routeToSearchURL({ filtersToAdd, replace: true });
          } else {
            this.props.onClose({
              source,
              callback: () => routeToSearchURL({ filtersToAdd, replace: true }),
            });
          }
        };

      onClear = () => {
        const filtersToRemove = [];
        this.state.selectedFilters.forEach((category) => {
          const categoryFilter = getFilter(category.uid);
          categoryFilter.subOptions = [];
          categoryFilter.value = '';
          filtersToRemove.push(categoryFilter);
        });

        if (!this.props.onClose) {
          routeToSearchURL({ filtersToAdd: filtersToRemove, replace: true });
        } else {
          this.props.onClose({
            source: 'clear button',
            callback: () =>
              routeToSearchURL({
                filtersToAdd: filtersToRemove,
                replace: true,
              }),
          });
        }
      };

      render() {
        return (
          <MoreFiltersAmountOfResultsProvider filters={this.getFilters()}>
            {({ amountOfResults, isLoading }) => (
              <WrappedComponent
                selectedFilters={this.state.selectedFilters}
                amountOfResults={amountOfResults}
                resultsLoading={isLoading}
                aggregations={this.state.aggregations}
                onApply={this.onApply}
                onClear={this.onClear}
                onKeywordAdd={this.onKeywordAdd}
                onKeywordRemove={this.onKeywordRemove}
                onFilterToggle={this.onFilterToggle}
                onToursToggle={this.onToursToggle}
                onToursSubcategoryToggle={this.onToursSubcategoryToggle}
                onActivitiesToggle={this.onActivitiesToggle}
                onActivitiesSubcategoryToggle={
                  this.onActivitiesSubcategoryToggle
                }
                onTransportModeSubOptionToggle={
                  this.onTransportModeSubOptionToggle
                }
                onTransportModeToggle={this.onTransportModeToggle}
                onPriceSubOptionToggle={this.onPriceSubOptionToggle}
                onDurationSubOptionToggle={this.onDurationSubOptionToggle}
              />
            )}
          </MoreFiltersAmountOfResultsProvider>
        );
      }
    };

    return withRouter(addFlux(klass));
  };
};

export default MoreFiltersConnector;
